// Keep in sync with chart-lib/src/utils/react-root.util.ts by hand until the
// shared @thoughtspot/react-compat-root package lands (tracked: SCAL-322015).
// Divergence: chart-lib uses renderIntoContainer/unmountContainer + an
// onRendered hook, and only flushSyncs the R18 root when a hook is passed;
// this copy is renderReactRoot/unmountReactRoot, no hook, and always
// flushSyncs the R18 root — every caller here is a top-level mount that
// needs the legacy synchronous-commit guarantee, not just the ones that
// happen to pass a callback.
// WeakMap contract: only renderReactRoot registers roots, so all renders must
// go through it — a stray ReactDOM.render leaves a root unmountReactRoot can't
// tear down.
//
// TEMPORARY React 18 migration shim. Concurrent-root rendering is gated on the
// server-authoritative nginx `$enableReact18` SSI flag (surfaced to the client
// as `window.__enableReact18`): only the server knows which react-dom bundle
// was actually served, so only it can safely gate `createRoot`. When the
// migration completes and the flag is retired, delete `getCreateRoot` and the
// `isReact18EnabledOnServer()` branch, leaving a plain ReactDOM render/unmount.

import ReactDOM, { flushSync } from 'react-dom';

interface CompatRoot {
    render(node: React.ReactElement): void;
    unmount(): void;
}

type CreateRootFn = (container: Element | DocumentFragment) => CompatRoot;

const rootsByContainer = new WeakMap<Element, CompatRoot>();

// nginx resolves `$enableReact18` at request time (default from the
// `nginx_enable_react18` gflag) and the bootstrap inline script materialises it
// as the boolean `window.__enableReact18`. Read via a string key so it needs no
// ambient typing — the same runtime-global convention as `window.libs`.
const SERVER_REACT18_FLAG_KEY = '__enableReact18';

/**
 * Reads the server-authoritative React 18 switch. When it is on, the React 18
 * CDN bundle was served and `window.libs.ReactDOMClientLib.createRoot` is
 * present before app JS runs, so no polling is needed.
 */
function isReact18EnabledOnServer(): boolean {
    return (globalThis as any)[SERVER_REACT18_FLAG_KEY] === true;
}

// `createRoot` lives in the `react-dom/client` entrypoint, which is absent from
// react-dom@17. It is read lazily off the runtime global the React 18 bundle
// injects (`window.libs.ReactDOMClientLib`) rather than statically imported,
// and cached on `globalThis` so every compiled copy of this module (blink-v2
// host + chart-lib) shares a single resolution.
const RESOLVED_CREATE_ROOT_KEY = '__tsCompatResolvedCreateRoot';

// Cache is intentionally sticky: the CDN chunk loads once per session, so the
// first resolved createRoot stays valid (no re-validation for dev-only HMR).
function getCreateRoot(): CreateRootFn | null {
    const g = globalThis as any;
    if (!g[RESOLVED_CREATE_ROOT_KEY]) {
        const createRoot = g?.libs?.ReactDOMClientLib?.createRoot;
        if (typeof createRoot === 'function') {
            g[RESOLVED_CREATE_ROOT_KEY] = createRoot;
        }
    }
    return (g[RESOLVED_CREATE_ROOT_KEY] as CreateRootFn) || null;
}

function createCompatRoot(container: Element): CompatRoot {
    // React 18 concurrent root only when the server flag is on. Flag off =>
    // never touch window.libs / createRoot; return a plain legacy wrapper.
    if (isReact18EnabledOnServer()) {
        const createRoot = getCreateRoot();
        if (createRoot) {
            return createRoot(container);
        }
    }
    return {
        render: node => {
            // eslint-disable-next-line react/no-deprecated
            ReactDOM.render(node, container);
        },
        unmount: () => {
            // eslint-disable-next-line react/no-deprecated
            ReactDOM.unmountComponentAtNode(container);
        },
    };
}

/**
 * Legacy ReactDOM.render is already synchronous, so callers here have always
 * been able to assume the tree (and its mount effects — session-loading
 * queries, cache writes, etc.) is committed before renderReactRoot returns.
 * The React 18 concurrent root schedules its initial commit asynchronously
 * instead, which silently breaks that assumption: with no flushSync, a mount
 * effect can still be pending when the caller proceeds, opening a window for
 * an unrelated unmount/remount (route change, viz re-key) to race it —
 * observed as duplicate session-load calls and the second one's response
 * getting normalized into the Apollo cache before the first one's cache
 * writes for the same nested entities have landed. flushSync closes that
 * window by forcing the R18 root to commit before returning, matching the
 * legacy root's behaviour exactly.
 */
export function renderReactRoot(
    node: React.ReactElement,
    container: Element,
): void {
    let root = rootsByContainer.get(container);
    if (!root) {
        root = createCompatRoot(container);
        rootsByContainer.set(container, root);
    }
    const compatRoot = root;
    if (isReact18EnabledOnServer()) {
        flushSync(() => compatRoot.render(node));
    } else {
        compatRoot.render(node);
    }
}

export function unmountReactRoot(container: Element): void {
    const root = rootsByContainer.get(container);
    if (root) {
        root.unmount();
        rootsByContainer.delete(container);
    }
}
